import {EventEmitter} from '@angular/core';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {TranslateService} from '@ngx-translate/core';
import {IndexManager} from './index.manager';
import {GeoFileLoaderManager, GeoFileLayer} from './geo-file-loader.manager';
import {MapLayersController} from '../controllers/map-layers.controller';
import {IndexType} from '@services/satellite-indices.service';
import {UploadedFile} from '@components/file-upload/file-upload.component';
import {INDEX_LEGENDS, LegendItem} from '../legend-configs';
import {IndexLayerOption} from '@shared/data/index-layer-options-data';

export interface OsmIndexLegendGeoOverlayHost {
  destroy$: Subject<void>;
  translate: TranslateService;
  indexManager: IndexManager;
  geoFileLoaderManager: GeoFileLoaderManager;
  indexLayerOptions: IndexLayerOption[];

  mapInstance: any;
  layersController: MapLayersController | undefined;
  isMapInitialized: boolean;

  selectedIndexLayer: string | null;
  selectedIndexDate: Date | null;
  indexAvailableDates: string[] | null;
  currentYear: number;
  isLoading: boolean;
  selectedWeatherLayer: string | null;
  indexLayerOpacity: number;
  indexLayerVisible: boolean;
  currentLegend: LegendItem[] | null;
  currentLegendTitle: string | null;
  legendCollapsed: boolean;
  showIndexModal: boolean;
  deferIndexApply: boolean;
  weatherLayerOpacity: number;

  geoFileUploadModalVisible: boolean;
  geoFileUploadType: 'gpkg' | 'geojson' | 'kml' | null;
  geoFileUploadAccept: string;
  isGeoFileLoading: boolean;
  loadedGeoFileLayers: GeoFileLayer[];

  indexLayerApplied: EventEmitter<void>;
  legendCloseClicked: EventEmitter<void>;

  setTimelineIndexLayersOpacity?(opacityPercent: number, visible: boolean): void;
}

export class OsmIndexLegendGeoOverlayManager {
  constructor(private readonly getHost: () => OsmIndexLegendGeoOverlayHost) {}

  clearIndexSelection(): void {
    const h = (this.getHost() as any);
    h.indexManager.removeIndexTileLayer();
    h.selectedIndexLayer = null;
    h.selectedIndexDate = null;
    h.indexAvailableDates = [];
    h.selectedWeatherLayer = null;
    h.indexLayerVisible = true;
    h.currentLegend = null;
    h.currentLegendTitle = null;
  }

  onLegendCloseClick(): void {
    this.clearIndexSelection();
    (this.getHost() as any).legendCollapsed = true;
    (this.getHost() as any).legendCloseClicked.emit();
  }

  getAvailableIndexDates(currentYear?: number): void {
    const h = (this.getHost() as any);
    if (currentYear) {
      h.currentYear = currentYear;
    }
    if (h.selectedIndexLayer) {
      h.indexManager
        .GetIndexDatesFromTileLayers(h.selectedIndexLayer as IndexType, h.currentYear)
        .pipe(takeUntil(h.destroy$))
        .subscribe((dates: string[]) => {
          h.indexAvailableDates = dates;
        });
    }
  }

  onIndexDateChange(): void {
    const h = (this.getHost() as any);
    if (h.deferIndexApply && h.showIndexModal) {
      return;
    }
    if (h.selectedIndexLayer === 'separator') {
      h.selectedIndexLayer = null;
      return;
    }

    h.indexManager.removeIndexTileLayer();
    this.getAvailableIndexDates();

    if (!h.selectedIndexLayer || !h.selectedIndexDate) {
      h.selectedWeatherLayer = null;
      h.currentLegend = null;
      return;
    }
    this.updateLegend(h.selectedIndexLayer);
    h.isLoading = true;
    const w = Number(h.indexLayerOpacity);
    const opacityPercent = Number.isFinite(w)
      ? Math.max(0, Math.min(100, w))
      : h.indexManager.getIndexTileOpacityPercent();
    h.indexLayerOpacity = opacityPercent;
    h.indexManager.rememberIndexTileOpacityPercent(opacityPercent);
    h.indexManager.addIndexTileLayer(
      h.selectedIndexLayer as string,
      h.selectedIndexDate as Date,
      opacityPercent,
      (loading: boolean) => {
        h.isLoading = loading;
        if (!loading) {
          h.selectedWeatherLayer = h.selectedIndexLayer;
          // Re-apply current slider opacity after tile reload (zoom pan, etc.) — not the value
          // captured when the layer was first added (stale closure would revert to default 60%).
          const currentOpacity = h.indexManager.getIndexTileOpacityPercent();
          h.indexLayerOpacity = currentOpacity;
          h.indexManager.setCurrentLayerOpacity(
            h.indexLayerVisible ? currentOpacity : 0,
            false,
          );
          h.indexManager.bringCurrentLayerToFront();
          h.indexLayerApplied.emit();
        }
      },
    );
  }

  openIndexModal(): void {
    const h = (this.getHost() as any);
    h.deferIndexApply = true;
    h.showIndexModal = true;
  }

  closeIndexModal(): void {
    const h = (this.getHost() as any);
    h.deferIndexApply = false;
    h.showIndexModal = false;
  }

  applyIndexSelection(): void {
    const h = (this.getHost() as any);
    h.deferIndexApply = false;
    this.onIndexDateChange();
    if (h.mapInstance) {
      h.mapInstance.setZoom(6);
    }
    h.showIndexModal = false;
  }

  onLayerChange(layer: any): void {
    const h = (this.getHost() as any);
    h.selectedIndexLayer = layer;
    h.selectedIndexDate = null;
    this.getAvailableIndexDates();
  }

  onDateChange(date: Date | null): void {
    (this.getHost() as any).selectedIndexDate = date;
  }

  updateLegend(layerKey: string): void {
    const h = (this.getHost() as any);
    if (INDEX_LEGENDS[layerKey]) {
      h.currentLegend = INDEX_LEGENDS[layerKey];
      h.currentLegendTitle = layerKey;
      h.legendCollapsed = false;
    } else {
      h.currentLegend = null;
      h.currentLegendTitle = null;
    }
  }

  getLegendTitle(): string | null {
    const h = (this.getHost() as any);
    if (!h.currentLegendTitle) {
      return null;
    }
    const lang = h.translate.currentLang || 'en-US';
    const option = h.indexLayerOptions.find(
      (o: IndexLayerOption) => o.value?.toLowerCase() === h.currentLegendTitle?.toLowerCase(),
    );
    if (option && option.names && option.names[lang]) {
      return option.names[lang];
    }
    try {
      return h.translate.instant(h.currentLegendTitle.toUpperCase()) || h.currentLegendTitle.toUpperCase();
    } catch {
      return h.currentLegendTitle.toUpperCase();
    }
  }

  toggleLegend(): void {
    const h = (this.getHost() as any);
    if (!h.currentLegend) {
      return;
    }
    h.legendCollapsed = !h.legendCollapsed;
  }

  onWeatherOpacityChange(opacity: number): void {
    const h = (this.getHost() as any);
    if (!h.isMapInitialized || !h.mapInstance) {
      return;
    }
    const pct = Math.max(0, Math.min(100, opacity));
    h.weatherLayerOpacity = pct;
    h.indexLayerOpacity = pct;
    h.indexManager.rememberIndexTileOpacityPercent(pct);
    if (this.hasActiveIndexLayer()) {
      this.applyIndexTileOpacity(pct);
    }
  }

  hasActiveIndexLayer(): boolean {
    const h = (this.getHost() as any);
    return !!(h.selectedIndexLayer && h.selectedIndexDate);
  }

  hasVisibleIndexLayer(): boolean {
    return this.hasActiveIndexLayer() && (this.getHost() as any).indexLayerVisible;
  }

  getActiveIndexLayerLabel(): string | null {
    return this.hasActiveIndexLayer() ? this.getLegendTitle() : null;
  }

  setIndexLayerOpacity(opacity: number): void {
    const h = (this.getHost() as any);
    const nextOpacity = Math.max(0, Math.min(100, opacity));
    h.indexLayerOpacity = nextOpacity;
    this.applyIndexTileOpacity(nextOpacity);
  }

  setIndexLayerVisibility(visible: boolean): void {
    const h = (this.getHost() as any);
    h.indexLayerVisible = visible;
    this.applyIndexTileOpacity(h.indexLayerOpacity, visible);
  }

  private applyIndexTileOpacity(opacityPercent: number, visible?: boolean): void {
    const h = (this.getHost() as any);
    const isVisible = visible ?? h.indexLayerVisible;
    const pct = Math.max(0, Math.min(100, opacityPercent));
    h.indexManager.rememberIndexTileOpacityPercent(pct);
    h.indexManager.setCurrentLayerOpacity(isVisible ? pct : 0, isVisible);
    h.setTimelineIndexLayersOpacity?.(pct, isVisible);
  }

  bringIndexLayerToFront(): void {
    (this.getHost() as any).indexManager.bringCurrentLayerToFront();
  }

  startLayerMonitoring(): void {
    const h = (this.getHost() as any);
    if (!h.mapInstance) {
      return;
    }
    h.mapInstance.on('layeradd', () => {
      setTimeout(() => this.updateOverlayLayerState(), 150);
    });
    h.mapInstance.on('layerremove', () => {
      setTimeout(() => this.updateOverlayLayerState(), 150);
    });
  }

  updateOverlayLayerState(): void {
    const h = (this.getHost() as any);
    let layers: any[] = [];
    if (!h.layersController) {
      layers = [];
    } else {
      layers = h.layersController.getExtraTileLayers();
    }
    const indexUiActive = !!(h.selectedIndexLayer && h.selectedIndexDate);
    if (layers.length > 0) {
      if (!indexUiActive || h.selectedWeatherLayer !== h.selectedIndexLayer) {
        h.selectedWeatherLayer = 'overlay';
      }
    } else {
      if (!indexUiActive) {
        h.selectedWeatherLayer = null;
      }
    }
  }

  openGeoFileUploadModal(type: 'geojson' | 'kml'): void {
    const h = (this.getHost() as any);
    h.geoFileUploadType = type;
    h.geoFileUploadAccept = type === 'kml' ? '.kml' : '.geojson,.json';
    h.geoFileUploadModalVisible = true;
  }

  closeGeoFileUploadModal(): void {
    const h = (this.getHost() as any);
    h.geoFileUploadModalVisible = false;
    h.geoFileUploadType = null;
    h.geoFileUploadAccept = '';
  }

  openGeoFileUploadModalCombined(): void {
    const h = (this.getHost() as any);
    h.geoFileUploadType = null;
    h.geoFileUploadAccept = '';
    h.geoFileUploadModalVisible = true;
  }

  selectGeoFileType(type: 'geojson' | 'kml'): void {
    const h = (this.getHost() as any);
    h.geoFileUploadType = type;
    h.geoFileUploadAccept = type === 'kml' ? '.kml' : '.geojson,.json';
  }

  async onGeoFileSelected(files: UploadedFile[]): Promise<void> {
    const h = (this.getHost() as any);
    if (!files || files.length === 0) {
      return;
    }
    const file = files[0].file;
    h.isGeoFileLoading = true;
    try {
      const geoFileLayer = await h.geoFileLoaderManager.loadFile(file);
      if (geoFileLayer) {
        h.loadedGeoFileLayers = h.geoFileLoaderManager.getLoadedLayers();
        console.log(`Loaded ${geoFileLayer.featureCount} features from ${geoFileLayer.name}`);
      }
      this.closeGeoFileUploadModal();
    } catch (error) {
      console.error('Error loading geo file:', error);
    } finally {
      h.isGeoFileLoading = false;
    }
  }

  removeGeoFileLayer(layerId: string): void {
    const h = (this.getHost() as any);
    h.geoFileLoaderManager.removeLayer(layerId);
    h.loadedGeoFileLayers = h.geoFileLoaderManager.getLoadedLayers();
  }

  removeAllGeoFileLayers(): void {
    const h = (this.getHost() as any);
    h.geoFileLoaderManager.removeAllLayers();
    h.loadedGeoFileLayers = [];
  }
}
